/*
 * <cart-shipping-rate> keeps the shipping row of the cart totals column, and so the total
 * underneath it, equal to what checkout will actually charge.
 *
 * Liquid cannot read the store's shipping zones, so the column is first rendered with the
 * "Flat shipping rate" theme setting: a hand-typed copy of the real rate, and one that
 * drifted. The setting said 89 while Settings › Shipping charged 149, so the drawer quoted
 * one figure and checkout another. This element asks Shopify for the live rates of the
 * current cart (the same lookup checkout performs) and rewrites both rows once they arrive.
 * The server-rendered figures stay in place as the fallback when scripting is off or the
 * request fails.
 *
 * Rendered by snippets/cart-totals.liquid, inside both the cart drawer and the cart page.
 * Each of those replaces the column via innerHTML after every cart change, which recreates
 * this element, so every connect is a fresh lookup for the cart as it now stands.
 */
(function () {
  if (customElements.get('cart-shipping-rate')) return;

  /*
   * Formats minor units the way the `money` filter does for the merchant's own currency
   * format, with one deliberate difference borrowed from snippets/money-exact.liquid: a
   * "no decimals" format still shows the agorot when there are any, so the row never rounds
   * to a figure checkout will not charge.
   */
  function formatMoney(cents, format) {
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';

    let thousands = ',';
    let decimal = '.';
    if (key.indexOf('comma_separator') !== -1) {
      thousands = '.';
      decimal = ',';
    } else if (key.indexOf('apostrophe_separator') !== -1) {
      thousands = "'";
    } else if (key.indexOf('space_separator') !== -1) {
      thousands = ' ';
      if (key.indexOf('period_and_space') === -1) decimal = ',';
    }

    const whole = Math.floor(cents / 100);
    const rest = cents % 100;
    let number = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (key.indexOf('no_decimals') === -1 || rest !== 0) {
      number += decimal + (rest < 10 ? '0' : '') + rest;
    }
    return match ? format.replace(match[0], number) : number;
  }

  class CartShippingRate extends HTMLElement {
    connectedCallback() {
      this.shippingElement = this.querySelector('[data-shipping-value]');
      this.totalElement = this.querySelector('[data-total-value]');
      const country = this.dataset.country;
      if (!country || !this.shippingElement || !this.totalElement) return;

      this.controller = new AbortController();
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const url = `${cartUrl}/shipping_rates.json?shipping_address[country]=${encodeURIComponent(country)}`;

      fetch(url, { headers: { Accept: 'application/json' }, signal: this.controller.signal })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((payload) => this.apply(payload.shipping_rates || []))
        .catch(() => {
          // The server-rendered fallback stays on screen.
        });
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    apply(rates) {
      if (!this.isConnected || !rates.length) return;

      // Checkout preselects the cheapest option, so that is the one the shopper pays.
      const cents = Math.min(...rates.map((rate) => Math.round(parseFloat(rate.price) * 100)));
      if (!Number.isFinite(cents)) return;

      const itemsTotal = parseInt(this.dataset.itemsTotal, 10) || 0;
      const format = this.dataset.moneyFormat || '{{amount}}';

      if (cents === 0) {
        this.shippingElement.textContent = this.dataset.freeLabel || '';
      } else {
        this.shippingElement.innerHTML = formatMoney(cents, format);
      }
      this.totalElement.innerHTML = formatMoney(itemsTotal + cents, format);
    }
  }

  customElements.define('cart-shipping-rate', CartShippingRate);
  window.CartShippingRate = { formatMoney };
})();
